import type { Currency } from './account.js';
import type { IsoTimestamp } from './common.js';

// SMS Lead Bot Phase 1 — the vehicle feed.
//
// One `vehicleListings` doc per unit of stock, per account. Owned by the
// leadBot module. Stock belongs to the BUSINESS rather than to a bot, so it is
// keyed per ACCOUNT exactly as the knowledge base is.
//
// ⚠️⚠️ THE DESCRIPTION STORED HERE IS ALREADY STRIPPED OF LEGAL CLAIMS, and the
// RAW text is deliberately NOT kept. A live Camperagent listing reads "you only
// need a car licence to drive it" — a legal claim contingent on that unit's GVM
// and on the reader's own licence class. Grounding an SMS reply in description
// text means the model will surface exactly that sentence, and a quoted legal
// claim arriving by SMS reads as ADVICE to the person receiving it. So the
// ingest strips those passages (src/lib/leadBot/listingClaims.ts) and this doc
// holds only what is safe to ground an answer in.
//
// ⚠️ NOT STORING THE RAW TEXT IS THE POINT, not an oversight: a second field
// carrying the original would be a field someone could later render into a
// prompt without thinking. Re-running an improved stripper means re-uploading
// the CSV, which is a manual step anyway.

/**
 * ⚠️ EXTRACTED FOR SEARCH, NEVER QUOTED. The extract/ground split from the
 * build plan: an extraction pass is itself a place facts go wrong, and the bot
 * would then state the extracted value with total confidence. These fields
 * decide WHICH unit is being discussed; the ANSWER comes from `description`.
 */
export interface VehicleListingAttributes {
  make: string | null;
  model: string | null;
  year: number | null;
  condition: string | null;
  priceCents: number | null;
  currency: Currency | null;
  berths: number | null;
  odometerKm: number | null;
  lengthMm: number | null;
  /**
   * ⚠️⚠️ STORED FOR SEARCH AND FORBIDDEN FROM THE PROMPT. A GVM figure in the
   * prompt is exactly the weight claim the stripper exists to remove — the
   * model would repeat it, and a mass rating by SMS is a compliance answer.
   * `listingIndex.ts` never renders it, and a test asserts that.
   */
  gvmKg: number | null;
}

export interface VehicleListing extends VehicleListingAttributes {
  id: string;
  accountId: string;
  /** The feed's own id (vehicle_id / stock number). Stable across uploads. */
  externalId: string;
  title: string;
  url: string | null;

  /** ⚠️ ALREADY STRIPPED. See the file header. */
  description: string;
  /**
   * The passages removed, verbatim. Recorded so the stripping is VISIBLE
   * rather than silent — an AM can see what the bot was not allowed to read.
   */
  strippedClaims: string[];

  /**
   * ⚠️ An upload REPLACES the account's stock set. Listings absent from the new
   * file go INACTIVE rather than being deleted, so a conversation that already
   * referenced one does not break.
   */
  isActive: boolean;
  /** Which upload last saw this listing. */
  uploadBatchId: string;

  createdAt: IsoTimestamp;
  updatedAt: IsoTimestamp;
  deactivatedAt: IsoTimestamp | null;
}

// ---------------------------------------------------------------------------
// upload
// ---------------------------------------------------------------------------

/** The fields an upload can map a CSV column onto. */
export type ListingFieldKey =
  | 'externalId'
  | 'title'
  | 'url'
  | 'make'
  | 'model'
  | 'year'
  | 'condition'
  | 'price'
  | 'description'
  | 'berths'
  | 'odometerKm'
  | 'lengthMm';

export interface ListingFieldDef {
  key: ListingFieldKey;
  label: string;
  required: boolean;
  /** Header spellings seen in real feeds, for the auto-suggest. */
  aliases: string[];
  hint: string;
}

/**
 * ⚠️ `drivetrain`, `fuel_type` and `transmission` are DELIBERATELY ABSENT. In
 * the live feed all three are literally the string "OTHER" on every row, so a
 * column for them would be a field that always holds a useless value and a
 * search facet that never narrows anything. Build nothing on them.
 */
export const LISTING_FIELDS: ListingFieldDef[] = [
  {
    key: 'externalId',
    label: 'Stock ID',
    required: true,
    aliases: ['vehicle_id', 'vehicleid', 'stock_number', 'stocknumber', 'stock no', 'id', 'vin'],
    hint: "The feed's own id for the unit. Used to match a listing across uploads.",
  },
  {
    key: 'title',
    label: 'Title',
    required: true,
    aliases: ['title', 'name', 'headline', 'vehicle'],
    hint: 'What the unit is called on the website.',
  },
  {
    key: 'description',
    label: 'Description',
    required: true,
    aliases: ['description', 'details', 'body', 'copy', 'long_description'],
    hint: 'The listing prose. This is what the bot grounds its answers in.',
  },
  {
    key: 'url',
    label: 'Listing URL',
    required: false,
    aliases: ['url', 'link', 'listing_url', 'permalink', 'web_url'],
    hint: 'Sent to the customer so they can see the unit themselves.',
  },
  {
    key: 'price',
    label: 'Price',
    required: false,
    aliases: ['price', 'sale_price', 'amount', 'cost'],
    hint: 'Handles "134490.0 AUD" as well as a plain number.',
  },
  {
    key: 'make',
    label: 'Make',
    required: false,
    aliases: ['make', 'manufacturer', 'brand'],
    hint: 'Used to work out which unit a customer is asking about.',
  },
  {
    key: 'model',
    label: 'Model',
    required: false,
    aliases: ['model', 'series'],
    hint: 'Used to work out which unit a customer is asking about.',
  },
  {
    key: 'year',
    label: 'Year',
    required: false,
    aliases: ['year', 'model_year', 'build_year'],
    hint: '',
  },
  {
    key: 'condition',
    label: 'Condition',
    required: false,
    aliases: ['condition', 'new_used', 'status'],
    hint: 'New, used, demo.',
  },
  {
    key: 'berths',
    label: 'Berths',
    required: false,
    aliases: ['berths', 'sleeps', 'beds', 'berth'],
    hint: 'If your feed has no berths column it is read from the description instead.',
  },
  {
    key: 'odometerKm',
    label: 'Odometer (km)',
    required: false,
    aliases: ['odometer', 'kilometres', 'kilometers', 'kms', 'km', 'mileage'],
    hint: '',
  },
  {
    key: 'lengthMm',
    label: 'Length (mm)',
    required: false,
    aliases: ['length', 'length_mm', 'overall_length'],
    hint: '',
  },
];

export const REQUIRED_LISTING_FIELDS: ListingFieldKey[] = LISTING_FIELDS.filter(
  (f) => f.required,
).map((f) => f.key);

/** One CSV row, already mapped onto field keys. Values are raw strings. */
export type MappedListingRow = Partial<Record<ListingFieldKey, string>>;

/** A row that could not be turned into a listing, and why. Reported, never dropped silently. */
export interface ListingRowProblem {
  rowNumber: number;
  reason: string;
}

export interface ListingUploadResult {
  batchId: string;
  uploadedAt: IsoTimestamp;
  created: number;
  updated: number;
  /** ⚠️ Listings absent from this file, now inactive. Never deleted. */
  deactivated: number;
  skipped: ListingRowProblem[];
  /** How many listings had a legal claim stripped from their description. */
  listingsWithStrippedClaims: number;
}

/** What the settings page shows about an account's current stock. */
export interface ListingStockSummary {
  accountId: string;
  activeCount: number;
  inactiveCount: number;
  /** ⚠️ Shown prominently: stock is a manual upload and stale by construction. */
  lastUploadedAt: IsoTimestamp | null;
  listingsWithStrippedClaims: number;
}
